import React, { useState, useEffect, useRef } from 'react';
import './App-layout.css';
import io from 'socket.io-client';
import { GAME_TICK_MS } from './shared/constants';
import MapComponent from './Map';
import { GameState, Fleet, FleetId, PlayerId, StarSystem, SystemType, CombatResult, CharacterRole } from './types';
import HUD from './components/HUD';
import IntelDock from './components/IntelDock';
import CommandConsole from './components/CommandConsole/CommandConsole';
import EconomyScreen from './components/EconomyScreen';
import BattleViewer from './components/BattleViewer';
import Tooltip from './ui/Tooltip';
import Splash from './components/Splash';

// Lightweight client-side copies of server lobby types
type LobbyMeta = {
  id: string;
  name?: string;
  status: 'waiting' | 'in_progress' | 'finished';
  createdAt: number;
  startedAt?: number;
  lastActiveAt: number;
  players: Record<string, { name?: string; connected?: boolean }>;
  // optional modern fields
  version?: number;
  isPublic?: boolean;
  maxPlayers?: number;
  hasPassword?: boolean;
};
type LobbySummary = { id: string; tick: number | null; meta: LobbyMeta | null };

function resolveServerUrl(): string {
  const envUrl = (process as any).env.REACT_APP_SOCKET_URL as string | undefined;
  if (envUrl && envUrl.trim()) return envUrl.trim();
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3002';
    }
    // In production (Render), use same-origin to avoid CORS
    return window.location.origin;
  }
  return 'http://localhost:3002';
}

const SERVER_URL = resolveServerUrl();
const SOCKET_PATH = (process.env.REACT_APP_SOCKET_PATH as string) || '/socket.io';
const FORCE_WS = (process.env.REACT_APP_WEBSOCKET_ONLY as string) === '1';
// Prefer websocket; allow polling as fallback unless explicitly disabled
const socket = io(SERVER_URL, {
  path: SOCKET_PATH,
  transports: FORCE_WS ? ['websocket'] : ['websocket', 'polling'],
  withCredentials: true,
});
(window as any).socket = socket;

function App() {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [myPlayerId, setMyPlayerId] = useState<PlayerId | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<StarSystem | null>(null);
  const [selectedFleets, setSelectedFleets] = useState<Fleet[]>([]);
  // Combat details now viewed in Battle Viewer; we still keep intel events
  const [combatLogMinimized, setCombatLogMinimized] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const [lastTickAt, setLastTickAt] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState<number>(Date.now());
  const [tickMs, setTickMs] = useState<number>(GAME_TICK_MS);
  const prevGameStateRef = useRef<GameState | null>(null);
  const eventIdRef = useRef<number>(0);
  type UIEvent = {
    id: number;
    tick: number;
    type: 'depart' | 'arrival' | 'combat_start' | 'combat_end' | 'combat_log' | 'politics' | 'construction' | 'world';
    text: string;
    systemId?: number;
    x?: number;
    y?: number;
  };
  const [events, setEvents] = useState<UIEvent[]>([]);
  const [ping, setPing] = useState<{ x: number; y: number } | null>(null);
  type RadialMenuState = {
    visible: boolean;
    x: number;
    y: number;
    type: 'fleet' | 'system' | null;
    fleet?: Fleet | null;
    system?: StarSystem | null;
  };
  const [radialMenu, setRadialMenu] = useState<RadialMenuState>({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null });
  const [awaitingMoveFleet, setAwaitingMoveFleet] = useState<Fleet | null>(null);
  const [activeTop, setActiveTop] = useState<'none' | 'senate' | 'characters' | 'economy'>('none');
  const [battleViewer, setBattleViewer] = useState<{ visible: boolean; systemId: number | null }>({ visible: false, systemId: null });
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [senateTarget, setSenateTarget] = useState<string>('');
  const [senateAmount, setSenateAmount] = useState<number>(100);
  const [voteSpend, setVoteSpend] = useState<number>(10);
  const [showProposalMenu, setShowProposalMenu] = useState<boolean>(false);
  const [proposalCost, setProposalCost] = useState<number>(25);
  const [proposalSystemId, setProposalSystemId] = useState<number | ''>('' as any);
  const [proposalExceptDuringCombat, setProposalExceptDuringCombat] = useState<boolean>(true);
  const [lobbyOpen, setLobbyOpen] = useState<boolean>(false);
  const [showSplash, setShowSplash] = useState<boolean>(true);
  const [lobbyId, setLobbyId] = useState<string>(() => localStorage.getItem('lobbyId') || 'default');
  const [playerName, setPlayerName] = useState<string>(() => localStorage.getItem('playerName') || '');
  const [lobbies, setLobbies] = useState<LobbySummary[]>([]);
  const [newLobbyName, setNewLobbyName] = useState<string>("");
  const [joinPassword, setJoinPassword] = useState<string>('');
  const [createPassword, setCreatePassword] = useState<string>('');
  const [createIsPublic, setCreateIsPublic] = useState<boolean>(true);
  const [createMaxPlayers, setCreateMaxPlayers] = useState<number>(8);
  const [customizeOpen, setCustomizeOpen] = useState<boolean>(false);
  const [prefColor, setPrefColor] = useState<string>(() => localStorage.getItem('prefColor') || '#66bbff');
  const [prefFleetIcon, setPrefFleetIcon] = useState<string>(() => localStorage.getItem('prefFleetIcon') || 'chevron');
  const [joinError, setJoinError] = useState<string>('');
  // Balance panel state (dev/testing)
  const [balanceOpen, setBalanceOpen] = useState<boolean>(false);
  const [balancePwd, setBalancePwd] = useState<string>(() => localStorage.getItem('balancePwd') || '');
  const [balanceData, setBalanceData] = useState<any>(null);
  const [balanceAuthenticated, setBalanceAuthenticated] = useState<boolean>(false);
  // Map display toggles
  const [showEmpireBorders, setShowEmpireBorders] = useState<boolean>(() => {
    try { const v = localStorage.getItem('ui.showEmpireBorders'); return v ? v === '1' : true; } catch { return true; }
  });
  const [showRingLabels, setShowRingLabels] = useState<boolean>(() => {
    try { const v = localStorage.getItem('ui.showRingLabels'); return v ? v === '1' : true; } catch { return true; }
  });
  const [playerKey, setPlayerKey] = useState<string>(() => {
    const existing = localStorage.getItem('playerKey');
    if (existing) return existing;
    const k = Math.random().toString(36).slice(2);
    localStorage.setItem('playerKey', k);
    return k;
  });

  // Persist map toggle preferences
  useEffect(() => {
    try { localStorage.setItem('ui.showEmpireBorders', showEmpireBorders ? '1' : '0'); } catch {}
  }, [showEmpireBorders]);
  useEffect(() => {
    try { localStorage.setItem('ui.showRingLabels', showRingLabels ? '1' : '0'); } catch {}
  }, [showRingLabels]);

  useEffect(() => {
    // Join default lobby on connect (can be replaced by a lobby UI later)
    socket.on('connect', () => {
      // Fetch current lobby list on initial connect
      socket.emit('listLobbies');
    });

    socket.on('playerConnected', ({ playerId }: { playerId: PlayerId }) => {
      setMyPlayerId(playerId);
      setLobbyOpen(false);
      setJoinError('');
      setShowSplash(false);
      // Apply saved cosmetics automatically on connect
      try {
        const color = localStorage.getItem('prefColor') || undefined;
        const fleetIcon = localStorage.getItem('prefFleetIcon') || undefined;
        if (color || fleetIcon) {
          socket.emit('setPlayerCosmetics', { color, fleetIcon });
        }
      } catch {}
    });

    socket.on('serverConfig', ({ tickMs }: { tickMs: number }) => {
      if (typeof tickMs === 'number' && tickMs > 100) setTickMs(tickMs);
    });

    socket.on('gameStateUpdate', (newState: GameState) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Received gameStateUpdate:', newState);
        if (newState.systems && newState.systems.length > 0) {
          console.log('First system data:', newState.systems[0]);
        }
      }
      // Generate basic UI events by diffing against previous state
      const prev = prevGameStateRef.current;
      if (prev) {
        try {
          const prevFleets = new Map(prev.fleets.map(f => [f.id, f]));
          const prevQueues = new Map(prev.systems.map(s => [s.id, (s.constructionQueue || []).map(j => ({ type: j.type, completionTick: j.completionTick }))]));
          const newEvents: UIEvent[] = [];
          for (const f of newState.fleets) {
            const p = prevFleets.get(f.id);
            if (!p) continue;
            // Departed
            if ((p.inTransit === false || !p.inTransit) && f.inTransit) {
              const origin = f.origin !== undefined ? newState.systems.find(s => s.id === f.origin) : undefined;
              const dest = f.destination !== undefined ? newState.systems.find(s => s.id === f.destination) : undefined;
              const id = ++eventIdRef.current;
              newEvents.push({
                id,
                tick: newState.tick,
                type: 'depart',
                text: `Fleet ${f.id} departed ${origin?.name ?? 'Unknown'} → ${dest?.name ?? 'Unknown'} (ETA ${Math.max(0, (f.arrivalTick ?? newState.tick) - newState.tick)}t)`,
                systemId: origin?.id,
                x: origin?.position.x,
                y: origin?.position.y,
              });
            }
            // Arrived
            if (p.inTransit && (f.inTransit === false || !f.inTransit)) {
              const sys = newState.systems.find(s => s.id === f.location);
              const id = ++eventIdRef.current;
              newEvents.push({
                id,
                tick: newState.tick,
                type: 'arrival',
                text: `Fleet ${f.id} arrived at ${sys?.name ?? 'Unknown'}`,
                systemId: sys?.id,
                x: sys?.position.x,
                y: sys?.position.y,
              });
            }
            // Combat start/end
            if ((p.inCombat === false || !p.inCombat) && f.inCombat) {
              const sys = newState.systems.find(s => s.id === f.location);
              const id = ++eventIdRef.current;
              newEvents.push({ id, tick: newState.tick, type: 'combat_start', text: `Combat started at ${sys?.name ?? 'Unknown'}`, systemId: sys?.id, x: sys?.position.x, y: sys?.position.y });
            }
            if (p.inCombat && (f.inCombat === false || !f.inCombat)) {
              const sys = newState.systems.find(s => s.id === f.location);
              const id = ++eventIdRef.current;
              newEvents.push({ id, tick: newState.tick, type: 'combat_end', text: `Combat ended at ${sys?.name ?? 'Unknown'}`, systemId: sys?.id, x: sys?.position.x, y: sys?.position.y });
            }
          }
          // Construction queue diffs
          try {
            for (const sys of newState.systems) {
              const before = prevQueues.get(sys.id) || [];
              const after = (sys.constructionQueue || []).map(j => ({ type: j.type, completionTick: j.completionTick }));
              // Suppress queued build notifications in Intel
              // Completed jobs (when previous first job is gone and its completionTick <= tick)
              if (before.length > 0) {
                const firstBefore = before[0];
                const stillThere = after.find(a => a.type === firstBefore.type && a.completionTick === firstBefore.completionTick);
                if (!stillThere && firstBefore.completionTick <= newState.tick) {
                  const id = ++eventIdRef.current;
                  newEvents.push({ id, tick: newState.tick, type: 'construction', text: `Completed ${firstBefore.type.toUpperCase()} at ${sys.name}`, systemId: sys.id, x: sys.position.x, y: sys.position.y });
                }
              }
            }
          } catch {}
          if (newEvents.length) {
            setEvents(prevEvents => [...newEvents, ...prevEvents].slice(0, 30));
          }
        } catch (e) {
          console.warn('Event diff error:', e);
        }
      }
      setLastTickAt(Date.now());
      setGameState(newState);
      prevGameStateRef.current = newState;
    });

    // Connection status handlers
    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    socket.on('combatResult', (result: CombatResult) => {
      // Surface a compact event entry in Intel feed
      const gs = prevGameStateRef.current;
      const sys = gs?.systems.find(s => s.name === result.location);
      const id = ++eventIdRef.current;
      const label = result.type === 'combat_end' ? 'Combat concluded' : 'Combat round';
      const evType: UIEvent['type'] = result.type === 'combat_end' ? 'combat_end' : 'combat_log';
      setEvents((prevEvents: UIEvent[]) => [
        { id, tick: result.tick, type: evType, text: `${label} at ${result.location}`, systemId: sys?.id, x: sys?.position.x, y: sys?.position.y },
        ...prevEvents
      ].slice(0, 30));
    });

    socket.on('senateEvent', (ev: any) => {
      const id = ++eventIdRef.current;
      const label = ev.type === 'motion_started'
        ? `New motion: ${ev.motion?.title ?? 'Unknown'}`
        : ev.type === 'motion_resolved'
          ? `Motion ${ev.outcome}: ${ev.motion?.title ?? 'Unknown'} (Y ${ev.yea}/N ${ev.nay})`
          : ev.type === 'vote_spent'
            ? `Vote: ${String(ev.playerId).slice(0, 4)}… spent ${ev.spend} for ${ev.side}`
            : 'Senate update';
      const newEvent: UIEvent = { id, tick: ev.tick ?? (prevGameStateRef.current?.tick ?? 0), type: 'politics', text: label };
      setEvents((prevEvents: UIEvent[]) => [newEvent, ...prevEvents].slice(0, 30));
    });

    socket.on('worldEvent', (ev: any) => {
      const id = ++eventIdRef.current;
      const label = ev?.type === 'faction_defeated'
        ? `Faction subdued: ${ev?.name ?? ev?.factionId}`
        : 'World event';
      const newEvent: UIEvent = { id, tick: ev?.tick ?? (prevGameStateRef.current?.tick ?? 0), type: 'world', text: label };
      setEvents((prevEvents: UIEvent[]) => [newEvent, ...prevEvents].slice(0, 30));
    });

    socket.on('balanceData', (payload: any) => {
      setBalanceData(payload);
      setBalanceAuthenticated(true);
    });
    socket.on('balanceError', (_err: any) => {
      alert('Balance panel access denied. Invalid password.');
      setBalanceAuthenticated(false);
      setBalanceData(null);
    });
    socket.on('allGamesDeleted', (data: { count: number }) => {
      alert(`✅ Successfully deleted ${data.count} game(s).\n\nAll lobbies have been stopped, removed from memory, and files deleted. Resources freed.`);
      setBalanceOpen(false);
      setBalanceAuthenticated(false);
      // Refresh lobby list
      socket.emit('listLobbies');
    });

    // Lobby lifecycle events
    socket.on('lobbies', (summaries: LobbySummary[]) => {
      setLobbies(Array.isArray(summaries) ? summaries : []);
    });
    socket.on('lobbyDeleted', ({ id }: { id: string }) => {
      setLobbies(prev => prev.filter(s => s.id !== id));
      if (lobbyId === id) {
        // If you were targeting the deleted lobby, clear selection
        setLobbyId('default');
      }
    });
    socket.on('lobbyCreated', ({ id, meta }: { id: string; meta: LobbyMeta }) => {
      setLobbies(prev => {
        const existingIdx = prev.findIndex(l => l.id === id);
        const summary: LobbySummary = { id, tick: 0, meta };
        if (existingIdx >= 0) {
          const copy = prev.slice();
          copy[existingIdx] = summary;
          return copy;
        }
        return [summary, ...prev];
      });
      setLobbyId(id);
    });
    socket.on('lobbyMetaUpdate', (meta: LobbyMeta) => {
      setLobbies(prev => prev.map(s => (s.id === meta.id ? { ...s, meta } : s)));
    });
    socket.on('lobbyStarted', ({ id, meta }: { id: string; meta: LobbyMeta }) => {
      setLobbies(prev => prev.map(s => (s.id === id ? { ...s, meta } : s)));
    });
    socket.on('joinError', (err: { code: string; message: string }) => {
      setJoinError(err?.message || 'Unable to join lobby');
      setLobbyOpen(true);
      setShowSplash(false);
    });

    return () => {
      socket.off('playerConnected');
      socket.off('gameStateUpdate');
      socket.off('combatResult');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('senateEvent');
      socket.off('balanceData');
      socket.off('balanceError');
      socket.off('lobbies');
      socket.off('lobbyCreated');
      socket.off('lobbyMetaUpdate');
      socket.off('lobbyStarted');
      socket.off('lobbyDeleted');
      socket.off('joinError');
      socket.off('serverConfig');
    };
  }, []);

  // When lobby is closed without a game loaded, return to splash only if not already connected as a player
  useEffect(() => {
    if (!lobbyOpen && !gameState && !myPlayerId) {
      setShowSplash(true);
    }
  }, [lobbyOpen, gameState, myPlayerId]);

  // Auto-refresh lobby list when panel opens
  useEffect(() => {
    if (lobbyOpen) {
      socket.emit('listLobbies');
    }
  }, [lobbyOpen]);

  // Shared lobby overlay
  const LobbyOverlay: React.FC = () => (
    <div className="screen-overlay" onClick={() => {}}>
      <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Lobby" onClick={(e) => e.stopPropagation()}>
        <div className="screen-header">
          <div className="screen-title">Join Game</div>
          <div className="row" style={{ gap: 8, marginLeft: 'auto' }}>
            <button className="action-btn primary" type="button" onClick={quickStartLobby} title="Join the default lobby immediately">Quick Start</button>
            <button className="close-btn" onClick={() => setLobbyOpen(false)} title="Close">✕</button>
          </div>
        </div>
        <div className="screen-content">
          <form onSubmit={submitLobby} className="stack" style={{ gap: 8 }}>
            <label className="row between">
              <span>Player Name</span>
              <input autoFocus value={playerName} onChange={(e) => setPlayerName(e.target.value)} placeholder="Your name" />
            </label>
            <label className="row between">
              <span>Lobby ID</span>
              <input value={lobbyId} onChange={(e) => setLobbyId(e.target.value)} placeholder="default" onKeyDown={(e) => e.stopPropagation()} />
            </label>
            <label className="row between">
              <span>Password</span>
              <input value={joinPassword} onChange={(e) => setJoinPassword(e.target.value)} placeholder="(if required)" type="password" onKeyDown={(e) => e.stopPropagation()} />
            </label>
            {joinError && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{joinError}</div>}
            <div className="row between" style={{ opacity: 0.8, fontSize: 12 }}>
              <span>Rejoin Key</span>
              <code>{playerKey}</code>
            </div>
            <div className="stack" style={{ gap: 6, marginTop: 8 }}>
              <div className="row between">
                <strong>Available Lobbies</strong>
                <div className="row" style={{ gap: 6 }}>
                  <button type="button" className="action-btn" onClick={() => socket.emit('listLobbies')}>Refresh</button>
                  <button type="button" className="action-btn" onClick={() => setCustomizeOpen(true)}>Customize…</button>
                </div>
              </div>
              {lobbies.length === 0 ? (
                <div style={{ opacity: 0.7 }}>No saved lobbies yet.</div>
              ) : (
                <div className="stack" style={{ gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                  {lobbies.map(s => {
                    const connected = s.meta ? Object.values(s.meta.players || {}).filter(p => p.connected).length : null;
                    const cap = s.meta?.maxPlayers ?? 8;
                    const status = s.meta?.status || 'waiting';
                    const hasPw = !!s.meta?.hasPassword;
                    const isDefault = s.id === 'default';
                    return (
                      <div key={s.id} className="row between">
                        <div>
                          <strong>{s.meta?.name || s.id}</strong>
                          <span style={{ opacity: 0.75 }}>
                            {hasPw ? ' 🔒' : ''} — {status}
                            {typeof connected === 'number' ? ` • ${connected}/${cap}` : ''}
                            {typeof s.tick === 'number' ? ` • t${s.tick}` : ''}
                          </span>
                        </div>
                        <div className="row" style={{ gap: 6 }}>
                          <button
                            type="button"
                            className="action-btn"
                            disabled={hasPw && !joinPassword}
                            title={hasPw && !joinPassword ? 'Enter password above to join' : ''}
                            onClick={() => quickJoinLobby(s.id)}
                          >Join</button>
                          {s.meta?.status !== 'in_progress' && (
                            <>
                              <select defaultValue="standard" id={`startSel-${s.id}`} style={{ padding: '4px 6px' }}>
                                <option value="standard">8p</option>
                                <option value="small">4p</option>
                              </select>
                              <button type="button" className="action-btn secondary" onClick={() => {
                                const sel = (document.getElementById(`startSel-${s.id}`) as HTMLSelectElement | null)?.value as 'small' | 'standard' | undefined;
                                socket.emit('startLobby', { lobbyId: s.id, mapSize: sel });
                              }}>Start</button>
                              <button type="button" className="action-btn" onClick={() => {
                                const nm = prompt('Rename lobby', s.meta?.name || s.id);
                                if (nm && nm.trim()) socket.emit('setLobbyName', { lobbyId: s.id, name: nm.trim() });
                              }}>Rename</button>
                            </>
                          )}
                          <button
                            type="button"
                            className="action-btn danger"
                            disabled={isDefault}
                            title={isDefault ? 'Cannot delete the default lobby' : ''}
                            onClick={() => {
                              if (window.confirm(`Delete lobby ${s.meta?.name || s.id}? This cannot be undone.`)) {
                                socket.emit('deleteLobby', { lobbyId: s.id });
                              }
                            }}
                          >Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="row between" style={{ marginTop: 4, gap: 6, flexWrap: 'wrap' }}>
                <input value={newLobbyName} onChange={(e) => setNewLobbyName(e.target.value)} placeholder="New lobby name (optional)" onKeyDown={(e) => e.stopPropagation()} />
                <select defaultValue="standard" id="mapSizeSel" style={{ padding: '6px 8px' }} onKeyDown={(e) => e.stopPropagation()}>
                  <option value="standard">8-Player Map</option>
                  <option value="small">4-Player Map</option>
                </select>
                <select value={createMaxPlayers} onChange={(e) => setCreateMaxPlayers(Math.max(2, Math.min(16, Number(e.target.value) || 8)))} style={{ padding: '6px 8px' }} onKeyDown={(e) => e.stopPropagation()}>
                  {Array.from({ length: 15 }, (_, i) => i + 2).map(n => (
                    <option key={n} value={n}>{n} players</option>
                  ))}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={createIsPublic} onChange={(e) => setCreateIsPublic(e.target.checked)} onKeyDown={(e) => e.stopPropagation()} /> Public
                </label>
                <input value={createPassword} onChange={(e) => setCreatePassword(e.target.value)} placeholder="Set password (optional)" type="password" onKeyDown={(e) => e.stopPropagation()} />
                <button type="button" className="action-btn primary" onClick={() => {
                  const sel = (document.getElementById('mapSizeSel') as HTMLSelectElement | null)?.value as 'small' | 'standard' | undefined;
                  socket.emit('createLobby', { name: newLobbyName || undefined, mapSize: sel, isPublic: createIsPublic, maxPlayers: createMaxPlayers, password: createPassword || undefined });
                }}>Create Lobby</button>
              </div>
            </div>
            <div className="row right">
              <button className="action-btn primary" type="submit">Enter</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const submitLobby = (e: React.FormEvent) => {
    e.preventDefault();
    const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default';
    const nm = playerName && playerName.trim() ? playerName.trim() : '';
    localStorage.setItem('lobbyId', id);
    localStorage.setItem('playerName', nm);
    if (!localStorage.getItem('playerKey')) localStorage.setItem('playerKey', playerKey);
    setShowSplash(false);
    socket.emit('joinLobby', { lobbyId: id, playerKey, name: nm || undefined, password: joinPassword || undefined });
  };

  const quickJoinLobby = (id: string) => {
    const nm = playerName && playerName.trim() ? playerName.trim() : '';
    setLobbyId(id);
    localStorage.setItem('lobbyId', id);
    localStorage.setItem('playerName', nm);
    if (!localStorage.getItem('playerKey')) localStorage.setItem('playerKey', playerKey);
    setShowSplash(false);
    socket.emit('joinLobby', { lobbyId: id, playerKey, name: nm || undefined, password: joinPassword || undefined });
  };

  const quickStartLobby = () => {
    const nm = playerName && playerName.trim() ? playerName.trim() : `House ${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    setPlayerName(nm);
    localStorage.setItem('playerName', nm);
    if (!localStorage.getItem('playerKey')) localStorage.setItem('playerKey', playerKey);
    const id = 'default';
    setLobbyId(id);
    localStorage.setItem('lobbyId', id);
    socket.emit('joinLobby', { lobbyId: id, playerKey, name: nm });
  };

  const openLobbyFromSplash = () => {
    setShowSplash(false);
    setLobbyOpen(true);
    socket.emit('listLobbies');
  };

  // Keep selected entities synced with the latest gameState objects
  useEffect(() => {
    if (!gameState) return;
    // Refresh selected system reference so properties like constructionQueue update live
    setSelectedSystem(prev => {
      if (!prev) return prev;
      const updated = gameState.systems.find(s => s.id === prev.id) || null;
      return updated;
    });
    // Refresh selected fleets references as well
    setSelectedFleets(prev => {
      if (!prev.length) return prev;
      const refreshed = prev
        .map(f => gameState.fleets.find(nf => nf.id === f.id) || null)
        .filter((f): f is typeof prev[number] => Boolean(f));
      return refreshed;
    });
  }, [gameState]);

  // Lightweight timer to animate tick progress ring
  // Avoid running while lobby is open or before game state exists to prevent UI jitter
  useEffect(() => {
    if (!gameState || lobbyOpen) return;
    const id = setInterval(() => setNowTs(Date.now()), 100);
    return () => clearInterval(id);
  }, [gameState, lobbyOpen]);

  const handleBuildShip = (systemId: number, shipClass: 'frigate' | 'destroyer' | 'cruiser' | 'capital', targetFleetId?: number) => {
    if (myPlayerId) {
      socket.emit('buildShip', { systemId, shipClass, targetFleetId });
    }
  };

  const handleBuildShipyard = (systemId: number) => {
    console.log('Build shipyard clicked for system:', systemId);
    if (myPlayerId) {
      socket.emit('buildShipyard', { systemId });
      console.log('Emitted buildShipyard event');
    }
  };

  const handleRepairFleet = (fleetId: number) => {
    if (myPlayerId) {
      socket.emit('repairFleet', { fleetId });
    }
  };

  // Removed placeholder actions buildDefense/buildEconomy (no server handlers yet)

  const handleFleetContextMenu = (fleet: Fleet, clientX: number, clientY: number) => {
    if (fleet.owner !== myPlayerId) return;
    setRadialMenu({ visible: true, x: clientX, y: clientY, type: 'fleet', fleet, system: null });
    setAwaitingMoveFleet(null);
  };

  const handleSystemContextMenu = (system: StarSystem, clientX: number, clientY: number) => {
    setRadialMenu({ visible: true, x: clientX, y: clientY, type: 'system', system, fleet: null });
  };

  const doMoveAction = () => {
    if (!radialMenu.fleet) return;
    setAwaitingMoveFleet(radialMenu.fleet);
    setRadialMenu({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null });
  };

  const handleSystemClick = (system: StarSystem) => {
    if (awaitingMoveFleet) {
      socket.emit('moveFleet', { fleetId: awaitingMoveFleet.id, targetSystemId: system.id });
      setAwaitingMoveFleet(null);
      setSelectedSystem(system);
      setSelectedFleets([]);
      return;
    }
    console.log('System clicked:', system);
    setSelectedSystem(prev => (prev && prev.id === system.id ? null : system));
    setSelectedFleets([]);
  };

  const handleFleetClick = (fleet: Fleet) => {
    if (fleet.owner !== myPlayerId) {
      setSelectedSystem(null);
      setSelectedFleets([fleet]);
      return;
    }

    setSelectedFleets(prevSelected => {
      const isAlreadySelected = prevSelected.find(f => f.id === fleet.id);
      if (isAlreadySelected) {
        return prevSelected.filter(f => f.id !== fleet.id);
      } else {
        if (prevSelected.length > 0 && prevSelected[0].location !== fleet.location) {
          return [fleet];
        }
        return [...prevSelected, fleet];
      }
    });
    setSelectedSystem(null);
  };

  const handleCombineFleets = () => {
    if (selectedFleets.length < 2) return;
    const fleetIds = selectedFleets.map(f => f.id);
    socket.emit('combineFleets', { fleetIds });
    setSelectedFleets([]);
  };

  const clearSelection = () => {
    setSelectedSystem(null);
    setSelectedFleets([]);
    setAwaitingMoveFleet(null);
    setRadialMenu({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // While splash is visible, only allow Enter/Space to start
      if (showSplash) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLobbyFromSplash();
        }
        return;
      }
      // Determine typing using activeElement for robustness across bubbling
      const active = (document.activeElement as HTMLElement | null);
      const tag = (active?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || (active?.isContentEditable ?? false);

      // Always allow Escape to close overlays, even while typing
      if (e.key === 'Escape') {
        if (showHelp) { setShowHelp(false); return; }
        if (activeTop !== 'none') { setActiveTop('none'); return; }
        if (battleViewer.visible) { setBattleViewer({ visible: false, systemId: null }); return; }
        if (lobbyOpen) { setLobbyOpen(false); return; }
        if (!combatLogMinimized) { setCombatLogMinimized(true); return; }
        clearSelection();
        return;
      }

      // While the lobby is open, ignore all other hotkeys
      if (lobbyOpen) return;

      // Ignore hotkeys while typing in inputs/contentEditable
      if (isTyping) return;

      // Hotkeys
      if (e.key === '?' || (e.shiftKey && e.key === '/')) { setShowHelp(v => !v); }
      if (e.key === 'i' || e.key === 'I') { setCombatLogMinimized(m => !m); }
      if (e.key === 'l' || e.key === 'L') { setLobbyOpen(true); socket.emit('listLobbies'); }
      if (e.key === 's' || e.key === 'S') { setActiveTop(v => v === 'senate' ? 'none' : 'senate'); }
      if (e.key === 'c' || e.key === 'C') { setActiveTop(v => v === 'characters' ? 'none' : 'characters'); }
      if (e.key === 'e' || e.key === 'E') { setActiveTop(v => v === 'economy' ? 'none' : 'economy'); }
      if (e.key === 'b' || e.key === 'B') {
        const sys = selectedSystem || (gameState?.systems.find(s => gameState.fleets.some(f => f.location === s.id && f.inCombat)) || null);
        if (sys) setBattleViewer({ visible: true, systemId: sys.id });
      }
      if (e.key === 'd' || e.key === 'D') {
        // Broadcast a custom event the map listens for to toggle border debug
        try { window.dispatchEvent(new CustomEvent('pi:toggleBorderDebug')); } catch {}
      }
    };
    // Capture phase ensures we can short-circuit before other listeners
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true } as any);
  }, [activeTop, battleViewer.visible, lobbyOpen, selectedSystem, gameState, combatLogMinimized, showHelp, showSplash]);

  // Senate voting
  const castVote = (side: 'Yea' | 'Nay', spend: number) => {
    socket.emit('castVote', { side, spend });
  };

  if (!gameState) {
    return (
      <div className={`app-container ${(lobbyOpen || customizeOpen || showSplash) ? 'overlay-open' : ''}`}>
        <Splash visible={showSplash} onPlayNow={openLobbyFromSplash} />
        {lobbyOpen && <LobbyOverlay />}
        {customizeOpen && (
          <div className="screen-overlay" onClick={() => setCustomizeOpen(false)}>
            <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Player Customization" onClick={(e) => e.stopPropagation()}>
              <div className="screen-header">
                <div className="screen-title">Customize Player</div>
                <button className="close-btn" onClick={() => setCustomizeOpen(false)} title="Close">✕</button>
              </div>
              <div className="screen-content">
                <div className="stack" style={{ gap: 10 }}>
                  <label className="row between">
                    <span>Player Color</span>
                    <input type="color" value={prefColor} onChange={(e) => setPrefColor(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
                  </label>
                  <label className="row between">
                    <span>Fleet Icon</span>
                    <select value={prefFleetIcon} onChange={(e) => setPrefFleetIcon(e.target.value)} onKeyDown={(e) => e.stopPropagation()}>
                      <option value="chevron">Chevron</option>
                      <option value="triangle">Triangle</option>
                      <option value="diamond">Diamond</option>
                      <option value="wing">Wing</option>
                    </select>
                  </label>
                  <div className="row right" style={{ gap: 8 }}>
                    <button className="action-btn" onClick={() => setCustomizeOpen(false)}>Cancel</button>
                    <button className="action-btn primary" onClick={() => {
                      try {
                        localStorage.setItem('prefColor', prefColor);
                        localStorage.setItem('prefFleetIcon', prefFleetIcon);
                      } catch {}
                      socket.emit('setPlayerCosmetics', { color: prefColor, fleetIcon: prefFleetIcon });
                      setCustomizeOpen(false);
                    }}>Save</button>
                  </div>
                  <div style={{ opacity: 0.8, fontSize: 12 }}>
                    Your color tints your borders and how others see your fleets. Fleet icon changes your fleet marker shape (coming soon).
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {!showSplash && !lobbyOpen && !customizeOpen && (
          <div className="loading-screen">Connecting to game server...</div>
        )}
      </div>
    );
  }

  const currentPlayer = myPlayerId ? gameState.players.find(p => p.id === myPlayerId) : null;

  const getCombatLogEntryClass = (log: CombatResult) => {
    // Handle cases where log.type might be undefined
    if (log.type === 'combat_end' && log.winner === myPlayerId) return 'victory';
    if (log.type === 'combat_end' && log.winner && log.winner !== myPlayerId) return 'defeat';
    if (log.attacker === myPlayerId) return 'attack';
    return 'defense';
  };

  // Calculate empire statistics for quick reference
  const myOwnedSystems = gameState.systems.filter(s => s.owner === myPlayerId);
  const myFleets = gameState.fleets.filter(f => f.owner === myPlayerId);
  const totalFleetPower = myFleets.reduce((sum, fleet) => sum + Math.round(fleet.power), 0);
  const fleetsInCombat = myFleets.filter(f => f.inCombat).length;
  // Tick ring metrics
  const R = 10;
  const C = 2 * Math.PI * R;
  const progress = lastTickAt ? Math.max(0, Math.min(1, (nowTs - lastTickAt) / tickMs)) : 0;
  const dashOffset = C * (1 - progress);

  const handleEventClick = (ev: UIEvent) => {
    if (!gameState) return;
    let sys: StarSystem | undefined;
    if (typeof ev.systemId === 'number') {
      sys = gameState.systems.find(s => s.id === ev.systemId);
    }
    if (sys) {
      setSelectedSystem(sys);
      setPing({ x: sys.position.x, y: sys.position.y });
      setTimeout(() => setPing(null), 2000);
      // Open Battle Viewer for combat-related events
      if (ev.type === 'combat_log' || ev.type === 'combat_start') {
        setBattleViewer({ visible: true, systemId: sys.id });
      }
      // Also allow opening on combat_end to review outcome
      if (ev.type === 'combat_end') {
        setBattleViewer({ visible: true, systemId: sys.id });
      }
      return;
    }
    if (typeof ev.x === 'number' && typeof ev.y === 'number') {
      setPing({ x: ev.x, y: ev.y });
      setTimeout(() => setPing(null), 2000);
    }
  };

  const overlayOpen = lobbyOpen || customizeOpen || activeTop !== 'none' || battleViewer.visible || showHelp || Boolean(gameState.winner) || showSplash;

  return (
    <div className={`app-container ${overlayOpen ? 'overlay-open' : ''}`}>
      <Splash visible={showSplash} onPlayNow={openLobbyFromSplash} />
      {lobbyOpen && <LobbyOverlay />}
      {!isConnected && (
        <div className="reconnect-banner" title="Attempting to reconnect to the server...">
          Reconnecting...
        </div>
      )}
      <div className="top-bar">
        <HUD gameState={gameState} myPlayerId={myPlayerId} progress={progress} C={C} dashOffset={dashOffset} />
        <div style={{ marginLeft: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
          <button
            className="map-top-btn"
            onClick={() => { setLobbyOpen(true); socket.emit('listLobbies'); }}
            title="Open lobby list to switch or create a game"
          >Lobbies</button>
          <button
            className="map-top-btn"
            onClick={() => {
              setBalanceOpen(true);
            }}
            title="Open Admin Panel"
          >⚖︎</button>
        </div>
      </div>

      <div className="main-content">
        <div className="map-container">
          <div className="map-top-panel">
            <Tooltip content="Open Senate">
              <button
                className={`map-top-btn ${activeTop === 'senate' ? 'active' : ''}`}
                onClick={() => setActiveTop(activeTop === 'senate' ? 'none' : 'senate')}
              >
                Senate
              </button>
            </Tooltip>
            <button
              className={`map-top-btn ${activeTop === 'characters' ? 'active' : ''}`}
              onClick={() => setActiveTop(activeTop === 'characters' ? 'none' : 'characters')}
            >
              <Tooltip content="Open Characters"><span>Characters</span></Tooltip>
            </button>
            <button
              className={`map-top-btn ${activeTop === 'economy' ? 'active' : ''}`}
              onClick={() => setActiveTop(activeTop === 'economy' ? 'none' : 'economy')}
            >
              <Tooltip content="Open Economy"><span>Economy</span></Tooltip>
            </button>
            <Tooltip content="Open Battle Viewer">
              <button
                className="map-top-btn"
                onClick={() => {
                  const sys = selectedSystem || (gameState.systems.find(s => gameState.fleets.some(f => f.location === s.id && f.inCombat)) || null);
                  if (sys) setBattleViewer({ visible: true, systemId: sys.id });
                  else alert('No active battle at the selected system.');
                }}
              >
                Battle
              </button>
            </Tooltip>
            
            {process.env.NODE_ENV !== 'production' && (
              <Tooltip content="Reset game to initial state (dev)">
                <button
                  className="map-top-btn"
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    if (window.confirm('Reset game to start?')) {
                      socket.emit('resetGame');
                    }
                  }}
                >
                  Reset
                </button>
              </Tooltip>
            )}
          </div>
          <div className="map-stage">
            <div className="starfield" />
            <MapComponent 
              gameState={gameState}
              onSystemClick={handleSystemClick}
              onFleetClick={handleFleetClick}
              myPlayerId={myPlayerId}
              selectedFleets={selectedFleets.map(f => f.id)}
              selectedSystemId={selectedSystem?.id ?? null}
              ping={ping}
              onFleetContextMenu={handleFleetContextMenu}
              onSystemContextMenu={handleSystemContextMenu}
              onEmptyClick={clearSelection}
              showRegionLabels={showRingLabels}
              showEmpireBorders={showEmpireBorders}
              tickFraction={progress}
            />
            {/* Bottom-right map toggles */}
            <div className="map-toggles" role="group" aria-label="Map Display Settings">
              <div className="toggle-item">
                <input id="tg-borders" type="checkbox" checked={showEmpireBorders} onChange={(e) => setShowEmpireBorders(e.target.checked)} />
                <label htmlFor="tg-borders">Empire borders</label>
              </div>
              <div className="toggle-item">
                <input id="tg-rings" type="checkbox" checked={showRingLabels} onChange={(e) => setShowRingLabels(e.target.checked)} />
                <label htmlFor="tg-rings">Ring labels</label>
              </div>
            </div>
            {/* Open battle viewer automatically when clicking a system with combat */}
            {selectedSystem && gameState.fleets.some(f => f.location === selectedSystem.id && f.inCombat) && (
              <button className="floating-btn" style={{ position: 'absolute', right: 12, bottom: 64 }} onClick={() => setBattleViewer({ visible: true, systemId: selectedSystem.id })}>Open Battle</button>
            )}
            {radialMenu.visible && (
              <>
                <div className="radial-overlay" onClick={() => setRadialMenu({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null })} />
                <div className="radial-menu" style={{ left: radialMenu.x, top: radialMenu.y }}>
                  <div className="rm-core">
                    {/* Center info */}
                    {radialMenu.type === 'fleet' && radialMenu.fleet && (
                      <div className="rm-center">
                        <div>Fleet #{radialMenu.fleet.id}</div>
                        <div>Pwr {Math.round(radialMenu.fleet.power)}/{Math.round(radialMenu.fleet.maxPower)}</div>
                      </div>
                    )}
                    {radialMenu.type === 'system' && radialMenu.system && (
                      <div className="rm-center">
                        <div>{radialMenu.system.name}</div>
                        <div style={{opacity:0.8}}>{radialMenu.system.owner ?? 'Unclaimed'}</div>
                      </div>
                    )}

                    {/* Actions */}
                    {radialMenu.type === 'fleet' && (
                      <>
                        <button className="rm-item rm-move" onClick={doMoveAction} title="Move">➡</button>
                        <button className="rm-item rm-patrol" disabled title="Patrol (coming soon)">🌀</button>
                        <button className="rm-item rm-rally" disabled title="Rally (coming soon)">📍</button>
                      </>
                    )}

                    {radialMenu.type === 'system' && radialMenu.system && (
                      <>
                        {/* Top: Move here (if awaiting a fleet), otherwise Select */}
                        {awaitingMoveFleet ? (
                          <button
                            className="rm-item rm-move"
                            onClick={() => {
                              socket.emit('moveFleet', { fleetId: awaitingMoveFleet.id, targetSystemId: radialMenu.system!.id });
                              setAwaitingMoveFleet(null);
                              setSelectedSystem(radialMenu.system!);
                              setRadialMenu({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null });
                            }}
                            title="Move Here"
                          >➡</button>
                        ) : (
                          <button
                            className="rm-item rm-move"
                            onClick={() => {
                              setSelectedSystem(radialMenu.system!);
                              setRadialMenu({ visible: false, x: 0, y: 0, type: null, fleet: null, system: null });
                            }}
                            title="Select"
                          >🎯</button>
                        )}

                        {/* Left/right: placeholders for future actions */}
                        <button className="rm-item rm-patrol" disabled title="Action (coming soon)">🛠️</button>
                        <button className="rm-item rm-rally" disabled title="Action (coming soon)">🏛️</button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Command Console (right drawer) */}
        <CommandConsole
          selectedSystem={selectedSystem}
          selectedFleets={selectedFleets}
          gameState={gameState}
          myPlayerId={myPlayerId}
          onBuildShip={(systemId, shipClass, targetFleetId) => socket.emit('buildShip', { systemId, shipClass, targetFleetId })}
          onBuildShipyard={(systemId) => socket.emit('buildShipyard', { systemId })}
          onRepairFleet={(fleetId) => socket.emit('repairFleet', { fleetId })}
          onMoveFleet={(fleetId, targetSystemId) => socket.emit('moveFleet', { fleetId, targetSystemId })}
          onCombineFleets={(fleetIds) => { socket.emit('combineFleets', { fleetIds }); setSelectedFleets([]); }}
          onDivideFleet={(fleetId, count) => { socket.emit('divideFleet', { fleetId, count }); setSelectedFleets([]); }}
          onAssignCharacterRole={(role, targetId) => {
            const me = myPlayerId ? gameState.players.find(p => p.id === myPlayerId) : null;
            if (!me) return;
            socket.emit('assignCharacterRole', { characterId: me.character.id, role, targetId });
          }}
        />

        {/* Intel Dock (bottom feed) */}
        <IntelDock
          events={events}
          minimized={combatLogMinimized}
          onToggleMinimized={() => setCombatLogMinimized(!combatLogMinimized)}
          onEventClick={handleEventClick}
          myPlayerId={myPlayerId}
          playerNamesById={Object.fromEntries(gameState.players.map(p => [p.id, p.name]))}
        />
        {battleViewer.visible && (
          <BattleViewer visible={battleViewer.visible} systemId={battleViewer.systemId} gameState={gameState} onClose={() => setBattleViewer({ visible: false, systemId: null })} />
        )}
      </div>
      {activeTop === 'senate' && (
        <div className="screen-overlay" onClick={() => setActiveTop('none')}>
          <div className="screen-panel senate" role="dialog" aria-modal="true" aria-label="Senate" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Senate</div>
              <button className="close-btn" onClick={() => setActiveTop('none')} title="Close">✕</button>
            </div>
            <div className="screen-content">
              {gameState.senate.currentMotion ? (
                (() => {
                  const m = gameState.senate.currentMotion!;
                  const votes = m.votes || ({} as Record<string, { yea: number; nay: number }>);
                  let yeaWeight = 0, nayWeight = 0;
                  Object.values(votes).forEach(rec => { yeaWeight += rec.yea || 0; nayWeight += rec.nay || 0; });
                  const myRec = myPlayerId ? votes[myPlayerId] : undefined;
                  const myInf = myPlayerId ? (gameState.players.find(p => p.id === myPlayerId)?.resources.influence ?? 0) : 0;
                  return (
                    <div className="senate-body">
                      <div className="proposal">
                        <div className="proposal-type">{m.title}</div>
                        <div className="proposal-details" style={{ opacity: 0.9 }}>{m.details}</div>
                        <div className="proposal-timer">Resolves in {gameState.senate.ticksUntilResolution} ticks</div>
                      </div>
                      <div className="vote-tally">
                        <div className="tally-row"><span>Yea</span><span>{yeaWeight} ✨</span></div>
                        <div className="tally-row"><span>Nay</span><span>{nayWeight} ✨</span></div>
                      </div>
                      <div className="my-vote" style={{ marginTop: 6 }}>
                        You: <strong>{(myRec?.yea ?? 0)} Yea / {(myRec?.nay ?? 0)} Nay</strong> • Influence: <strong>{myInf}</strong>
                      </div>
                      <div className="vote-buttons" style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                        <input type="number" min={1} value={voteSpend} onChange={(e) => setVoteSpend(Math.max(1, Number(e.target.value) || 1))} style={{ width: 80 }} />
                        <button className="action-btn primary" disabled={!myPlayerId || voteSpend <= 0 || myInf <= 0} onClick={() => castVote('Yea', voteSpend)}>Spend {voteSpend} ✨ for Yea</button>
                        <button className="action-btn secondary" disabled={!myPlayerId || voteSpend <= 0 || myInf <= 0} onClick={() => castVote('Nay', voteSpend)}>Spend {voteSpend} ✨ for Nay</button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="senate-empty">No active motion</div>
              )}
              <div className="senate-section" style={{ marginTop: 16 }}>
                <div className="section-header"><span className="section-title">Upcoming Motion</span></div>
                {gameState.senate.upcomingMotion ? (
                  <div className="proposal">
                    <div className="proposal-type">{gameState.senate.upcomingMotion.title}</div>
                    <div className="proposal-details" style={{ opacity: 0.9 }}>{gameState.senate.upcomingMotion.details}</div>
                  </div>
                ) : <div className="senate-empty">—</div>}
                  <div className="row right" style={{ marginTop: 8 }}>
                    <button className="action-btn" disabled={!myPlayerId} onClick={() => setShowProposalMenu(true)}>Propose a Law…</button>
                  </div>
              </div>
              <div className="senate-section" style={{ marginTop: 16 }}>
                <div className="section-header"><span className="section-title">Active Laws</span></div>
                {gameState.senate.activeLaws.length === 0 ? (
                  <div className="senate-empty">None</div>
                ) : (
                  <div className="stack">
                    {gameState.senate.activeLaws.map(law => {
                      const expiresIn = typeof law.expiresTick === 'number' ? Math.max(0, law.expiresTick - gameState.tick) : null;
                      return (
                        <div key={law.id} className="row between">
                          <div><strong>{law.title}</strong> — {law.description}</div>
                          <div style={{ opacity: 0.85 }}>{expiresIn != null ? `t-${expiresIn}` : 'Permanent'}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="senate-section" style={{ marginTop: 16 }}>
                <div className="section-header"><span className="section-title">Past Votes</span></div>
                {gameState.senate.pastVotes.length === 0 ? (
                  <div className="senate-empty">None</div>
                ) : (
                  <div className="stack">
                    {gameState.senate.pastVotes.map(v => (
                      <div key={v.id} className="row between">
                        <div><strong>{v.title}</strong> — {v.details}</div>
                        <div>{v.outcome} • Y {v.yea} / N {v.nay} • t{v.resolvedTick}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {customizeOpen && (
        <div className="screen-overlay" onClick={() => setCustomizeOpen(false)}>
          <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Player Customization" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Customize Player</div>
              <button className="close-btn" onClick={() => setCustomizeOpen(false)} title="Close">✕</button>
            </div>
            <div className="screen-content">
              <div className="stack" style={{ gap: 10 }}>
                <label className="row between">
                  <span>Player Color</span>
                  <input type="color" value={prefColor} onChange={(e) => setPrefColor(e.target.value)} />
                </label>
                <label className="row between">
                  <span>Fleet Icon</span>
                  <select value={prefFleetIcon} onChange={(e) => setPrefFleetIcon(e.target.value)}>
                    <option value="chevron">Chevron</option>
                    <option value="triangle">Triangle</option>
                    <option value="diamond">Diamond</option>
                    <option value="wing">Wing</option>
                  </select>
                </label>
                <div className="row right" style={{ gap: 8 }}>
                  <button className="action-btn" onClick={() => setCustomizeOpen(false)}>Cancel</button>
                  <button className="action-btn primary" onClick={() => {
                    try {
                      localStorage.setItem('prefColor', prefColor);
                      localStorage.setItem('prefFleetIcon', prefFleetIcon);
                    } catch {}
                    socket.emit('setPlayerCosmetics', { color: prefColor, fleetIcon: prefFleetIcon });
                    setCustomizeOpen(false);
                  }}>Save</button>
                </div>
                <div style={{ opacity: 0.8, fontSize: 12 }}>
                  Your color tints your borders and how others see your fleets. Fleet icon changes your fleet marker shape (coming soon).
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTop === 'senate' && showProposalMenu && (
        <div className="screen-overlay" onClick={() => setShowProposalMenu(false)}>
          <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Propose a Law" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Propose a Law</div>
              <button className="close-btn" onClick={() => setShowProposalMenu(false)} title="Close">✕</button>
            </div>
            <div className="screen-content">
              <div className="stack" style={{ gap: 12 }}>
                <div className="row between" style={{ alignItems: 'baseline' }}>
                  <div className="section-title">Player-Sponsored Proposals</div>
                  <label className="row" style={{ gap: 8 }}>
                    <span>Cost</span>
                    <input type="number" min={1} value={proposalCost} onChange={(e) => setProposalCost(Math.max(1, Number(e.target.value) || 1))} style={{ width: 80 }} />
                  </label>
                </div>
                <div className="law-grid">
                  {/* Writ of Trespass card */}
                  <div className="law-card">
                    <div className="law-title">Quarantine Order</div>
                    <div className="law-sub">Writ of Trespass (System)</div>
                    <div className="law-desc">Blocks non-owner fleets from occupying the target system. If the owner has defenders present, arrivals proceed to combat.</div>
                    <div className="law-fields stack" style={{ gap: 6, marginTop: 8 }}>
                      <label className="row between">
                        <span>Target System ID</span>
                        <input type="number" value={proposalSystemId as number | ''} onChange={(e) => setProposalSystemId(Number(e.target.value) || ('' as any))} placeholder="e.g., 2" />
                      </label>
                      <label className="row between" style={{ fontSize: 13 }}>
                        <span>Allow entry if defenders present</span>
                        <input type="checkbox" checked={proposalExceptDuringCombat} onChange={(e) => setProposalExceptDuringCombat(e.target.checked)} />
                      </label>
                    </div>
                    <div className="row between law-meta">
                      <div className="pill">Duration: 20 ticks</div>
                      <div className="pill">Cost: {proposalCost} ✨</div>
                    </div>
                    <div className="row right" style={{ marginTop: 8 }}>
                      <button className="action-btn primary" disabled={!myPlayerId || proposalSystemId === ''} onClick={() => {
                        const duration = 20;
                        socket.emit('proposeMotion', {
                          type: 'WritTrespass',
                          title: `Writ of Trespass: System ${proposalSystemId}`,
                          details: `Forbid occupation unless defenders present for ${duration} ticks`,
                          params: { duration, effect: { kind: 'WritTrespass', systemId: Number(proposalSystemId), exceptDuringCombat: proposalExceptDuringCombat } },
                          costInfluence: proposalCost,
                        });
                        setShowProposalMenu(false);
                      }}>Propose</button>
                    </div>
                  </div>

                  {/* Informational cards (not yet sponsor-able) */}
                  <div className="law-card disabled">
                    <div className="law-title">System Tax Relief</div>
                    <div className="law-sub">System-Level Income Boost</div>
                    <div className="law-desc">Temporarily boosts a specific world's credits income.</div>
                    <div className="law-note">Not yet sponsor-able</div>
                  </div>
                  <div className="law-card disabled">
                    <div className="law-title">Strategic Subsidy</div>
                    <div className="law-sub">Build Speed Aid</div>
                    <div className="law-desc">Reduces ship build time for a target player's shipyards.</div>
                    <div className="law-note">Not yet sponsor-able</div>
                  </div>
                  <div className="law-card disabled">
                    <div className="law-title">Fortress World Program</div>
                    <div className="law-sub">Fortification Boost</div>
                    <div className="law-desc">Boosts fortification regeneration on a target player's worlds.</div>
                    <div className="law-note">Not yet sponsor-able</div>
                  </div>
                  <div className="law-card disabled">
                    <div className="law-title">Trade Embargo</div>
                    <div className="law-sub">Targeted Economic Sanction</div>
                    <div className="law-desc">Reduces credits income for a target player.</div>
                    <div className="law-note">Not yet sponsor-able</div>
                  </div>
                </div>

                <div className="section-title" style={{ marginTop: 8 }}>Global/Regional Policies (Random Pool)</div>
                <div className="law-grid">
                  <div className="law-card info">
                    <div className="law-title">Open Hyperlanes</div>
                    <div className="law-sub">Travel-Time Reduction (Imperial)</div>
                    <div className="law-desc">Fleets travel 20% faster through Imperial systems.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Wildspace Interdiction</div>
                    <div className="law-sub">Travel-Time Increase (Wildspace)</div>
                    <div className="law-desc">Fleets travel 30% slower in Wildspace.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">No Bombardment Act</div>
                    <div className="law-sub">Combat Rule</div>
                    <div className="law-desc">Disables population bombardment across the map.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">War Bonds</div>
                    <div className="law-sub">Economy</div>
                    <div className="law-desc">Credits +20%, Influence −10%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Cultural Renaissance</div>
                    <div className="law-sub">Economy</div>
                    <div className="law-desc">Influence +25%, Credits −10%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Arms Limitation</div>
                    <div className="law-sub">Production</div>
                    <div className="law-desc">Increases ship build time by 25%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Emergency Repairs</div>
                    <div className="law-sub">Repair Speed</div>
                    <div className="law-desc">Reduces fleet repair time by 40%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Dockyard Overtime</div>
                    <div className="law-sub">Shipyard Build</div>
                    <div className="law-desc">Reduces shipyard construction time by 20%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Propaganda Blitz+</div>
                    <div className="law-sub">Influence</div>
                    <div className="law-desc">Increases influence income by 30%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                  <div className="law-card info">
                    <div className="law-title">Industrial Austerity</div>
                    <div className="law-sub">Economy</div>
                    <div className="law-desc">Reduces credits income by 15%.</div>
                    <div className="law-note">Appears randomly</div>
                  </div>
                </div>

                <div style={{ opacity: 0.8, fontSize: 12 }}>Note: Proposals replace the upcoming motion immediately and consume your influence.</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTop === 'characters' && (
        <div className="screen-overlay" onClick={() => setActiveTop('none')}>
          <div className="screen-panel characters" role="dialog" aria-modal="true" aria-label="Characters" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Characters</div>
              <button className="close-btn" onClick={() => setActiveTop('none')} title="Close">✕</button>
            </div>
            <div className="screen-content">
              {!currentPlayer ? (
                <div style={{ opacity: 0.85 }}>Connect to view your characters.</div>
              ) : (
                <div className="character-grid">
                  {(() => {
                    const chars = [currentPlayer.character];
                    if (!chars.length) {
                      return <div className="senate-empty">No characters yet.</div>;
                    }
                    return chars.map(c => {
                      const roleLabel = CharacterRole[c.role];
                      let appointment: React.ReactNode = <span>Unassigned</span>;
                      if (c.role === CharacterRole.Governor && c.assignment != null) {
                        const sys = gameState.systems.find(s => s.id === (c.assignment as number));
                        appointment = <span>Governor of {sys ? sys.name : `System ${c.assignment}`}</span>;
                      } else if (c.role === CharacterRole.Admiral && c.assignment != null) {
                        const fl = gameState.fleets.find(f => f.id === (c.assignment as number));
                        const loc = fl ? gameState.systems.find(s => s.id === fl.location)?.name : undefined;
                        appointment = <span>Admiral of Fleet F{c.assignment}{loc ? ` at ${loc}` : ''}</span>;
                      }
                      return (
                        <div key={c.id} className="char-card">
                          <div className="char-header">
                            <div className="char-name">{c.name}</div>
                            <div className="char-role">{roleLabel}</div>
                          </div>
                          <div className="char-assign">{appointment}</div>
                          <div className="char-stats">
                            <div className="stat-row">
                              <span>Admin:</span> <strong>{c.admin ?? 0}</strong>
                              {typeof c.adminXP === 'number' ? (
                                <span className="xp"> • XP {c.adminXP}</span>
                              ) : null}
                            </div>
                            <div className="stat-row">
                              <span>Military:</span> <strong>{c.military ?? 0}</strong>
                              {typeof c.militaryXP === 'number' ? (
                                <span className="xp"> • XP {c.militaryXP}</span>
                              ) : null}
                            </div>
                          </div>
                          <div className="char-meta">ID: {c.id}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {activeTop === 'economy' && (
        <div className="screen-overlay" onClick={() => setActiveTop('none')}>
          <EconomyScreen gameState={gameState} myPlayerId={myPlayerId} />
        </div>
      )}
      {balanceOpen && (
        <div className="screen-overlay" onClick={() => { setBalanceOpen(false); setBalanceAuthenticated(false); }}>
          <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Balance Panel" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Balance Panel</div>
              <button className="close-btn" onClick={() => { setBalanceOpen(false); setBalanceAuthenticated(false); }} title="Close">✕</button>
            </div>
            <div className="screen-content">
              <div className="stack" style={{ gap: 8 }}>
                <label className="row between">
                  <span>Password</span>
                  <input 
                    type="password" 
                    value={balancePwd} 
                    onChange={(e) => {
                      setBalancePwd(e.target.value);
                      setBalanceAuthenticated(false); // Reset auth when password changes
                    }} 
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') {
                        const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default';
                        socket.emit('getBalance', { lobbyId: id, password: balancePwd });
                      }
                    }} 
                  />
                </label>
                {!balanceAuthenticated ? (
                  <button className="action-btn primary" onClick={() => {
                    const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default';
                    socket.emit('getBalance', { lobbyId: id, password: balancePwd });
                  }}>🔓 Unlock Admin Panel</button>
                ) : (
                  <div style={{ padding: 8, backgroundColor: 'rgba(76, 175, 80, 0.2)', borderRadius: 4, color: '#4caf50' }}>
                    ✅ Admin panel unlocked
                  </div>
                )}
                {balanceAuthenticated && (
                  <div className="row" style={{ gap: 6 }}>
                    <button className="action-btn" onClick={() => {
                      const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default';
                      socket.emit('getBalance', { lobbyId: id, password: balancePwd });
                    }}>🔄 Refresh</button>
                    <button className="action-btn" style={{ backgroundColor: '#d32f2f' }} onClick={() => {
                      if (!window.confirm('⚠️ DELETE ALL RUNNING GAMES?\n\nThis will stop and permanently delete all active lobbies. This cannot be undone!\n\nAre you sure?')) return;
                      socket.emit('deleteAllGames', { password: balancePwd });
                    }}>🗑️ Delete All Games</button>
                  </div>
                )}
                {!balanceData && balanceAuthenticated ? (
                  <div style={{ opacity: 0.8 }}>Loading data...</div>
                ) : balanceAuthenticated && balanceData ? (
                  <div className="stack" style={{ gap: 10 }}>
                    <div style={{ opacity: 0.8 }}>Tick: {gameState?.tick ?? 0} • Server tick: ~{tickMs}ms</div>
                    {(() => {
                      const fields: Array<{ key: string; label: string; min?: number; max?: number; step?: number } > = [
                        { key: 'travelDistancePerTick', label: 'Travel Dist / Tick', min: 10, max: 10000, step: 10 },
                        { key: 'populationGrowthRate', label: 'Population Growth Rate', min: 0, max: 1, step: 0.01 },
                        { key: 'fortificationRegenRate', label: 'Fortification Regen Rate', min: 0, max: 1, step: 0.01 },
                        { key: 'bombardmentRateVsForts', label: 'Bombard Rate vs Forts', min: 0, max: 10, step: 0.01 },
                        { key: 'collateralRate', label: 'Collateral Rate', min: 0, max: 10, step: 0.01 },
                        { key: 'populationBombardRate', label: 'Population Bombard Rate', min: 0, max: 10, step: 0.01 },
                        { key: 'combatDpsMultiplier', label: 'Combat DPS Multiplier', min: 0.01, max: 10, step: 0.01 },
                      ];
                      const cur = balanceData.current || {};
                      const base = balanceData.base || {};
                      return (
                        <div className="stack" style={{ gap: 8 }}>
                          {fields.map(f => (
                            <label key={f.key} className="row between">
                              <span>{f.label} <span style={{ opacity: 0.7, fontSize: 12 }}>(base {base[f.key]})</span></span>
                              <input type="number" step={f.step as any} min={f.min as any} max={f.max as any}
                                value={cur[f.key] ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default';
                                  socket.emit('updateBalance', { lobbyId: id, password: balancePwd, changes: { [f.key]: Number(v) } });
                                }}
                                onKeyDown={(e) => e.stopPropagation()}
                                style={{ width: 140 }}
                              />
                            </label>
                          ))}
                          <div className="row between">
                            <span>Build Time Multipliers (fleet/shipyard/repair)</span>
                            <div className="row" style={{ gap: 6 }}>
                              <input type="number" step={0.1} value={balanceData.current?.buildTimeMultipliers?.fleet ?? 1}
                                onChange={(e) => { const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default'; socket.emit('updateBalance', { lobbyId: id, password: balancePwd, changes: { buildTimeMultipliers: { fleet: Number(e.target.value) } } }); }}
                                onKeyDown={(e) => e.stopPropagation()} style={{ width: 80 }} />
                              <input type="number" step={0.1} value={balanceData.current?.buildTimeMultipliers?.shipyard ?? 1}
                                onChange={(e) => { const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default'; socket.emit('updateBalance', { lobbyId: id, password: balancePwd, changes: { buildTimeMultipliers: { shipyard: Number(e.target.value) } } }); }}
                                onKeyDown={(e) => e.stopPropagation()} style={{ width: 80 }} />
                              <input type="number" step={0.1} value={balanceData.current?.buildTimeMultipliers?.repair ?? 1}
                                onChange={(e) => { const id = lobbyId && lobbyId.trim() ? lobbyId.trim() : 'default'; socket.emit('updateBalance', { lobbyId: id, password: balancePwd, changes: { buildTimeMultipliers: { repair: Number(e.target.value) } } }); }}
                                onKeyDown={(e) => e.stopPropagation()} style={{ width: 80 }} />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
      {gameState.winner && (
        <div className="screen-overlay" onClick={() => {}}>
          <div className="screen-panel gameover" role="dialog" aria-modal="true" aria-label="Game Over" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Game Over</div>
            </div>
            <div className="screen-content">
              <div style={{ fontSize: 18, marginBottom: 8 }}>Winner: <strong>{gameState.winner}</strong></div>
              <div style={{ opacity: 0.85, marginBottom: 12 }}>The Capital has been held long enough to claim the throne.</div>
              <div style={{ opacity: 0.8 }}>Restart the backend to reset the world for a new session.</div>
            </div>
          </div>
        </div>
      )}
      {showHelp && (
        <div className="screen-overlay" onClick={() => setShowHelp(false)}>
          <div className="screen-panel" role="dialog" aria-modal="true" aria-label="Help" onClick={(e) => e.stopPropagation()}>
            <div className="screen-header">
              <div className="screen-title">Controls</div>
              <button className="close-btn" onClick={() => setShowHelp(false)} title="Close">✕</button>
            </div>
            <div className="screen-content">
              <div className="stack" style={{ gap: 6 }}>
                <div>Pan: drag • Zoom: mouse wheel • Select: click • Context menu: right-click</div>
                <div><strong>Hotkeys</strong>: ? Help • I Intel • B Battle • L Lobbies • S Senate • C Characters • E Economy • Esc Close/Clear</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
